// ========================================
// 🧫 PROMPT PARA ANÁLISE DE SOROLOGIAS
// Foco: status viral/infeccioso, rastreio pré-Tx, risco pós-Tx, imunização.
// Análise SETORIAL (só sorologias) — espelha gerarPromptMarcadores (B313/AI_141).
// ========================================

import { CAMPOS_SOROLOGIAS_ROTULO } from "../../exames/data/camposSorologias";

// Ordem de exibição (agrupada por sistema), reutiliza as chaves dos rótulos.
const ORDEM_CAMPOS = Object.keys(CAMPOS_SOROLOGIAS_ROTULO);

export function gerarPromptSorologias(
  paciente: any,
  sorologias: any[]
): string {
  // Ordenar por data (mais antigo primeiro = cronológico)
  const ordenadas = [...sorologias].sort(
    (a, b) =>
      new Date(a.dataColeta).getTime() - new Date(b.dataColeta).getTime()
  );

  const formatarData = (data: string) => {
    if (!data) return "N/A";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  // 📏 Mesma regra "TODAS + teto generoso" do marcadores (B312-FU2): sorologias
  // mudam devagar, mas a série importa p/ HBV/HCV; teto só protege o caso extremo.
  const LIMITE_COLETAS = 15;
  const usadas =
    ordenadas.length > LIMITE_COLETAS
      ? ordenadas.slice(-LIMITE_COLETAS)
      : ordenadas;
  const omitidas = ordenadas.length - usadas.length;

  const historico = usadas
    .map((s, i) => {
      const partes: string[] = [];
      for (const campo of ORDEM_CAMPOS) {
        const v = s[campo];
        if (v === undefined || v === null || v === "") continue;
        const rot = CAMPOS_SOROLOGIAS_ROTULO[campo];
        const unid = rot.unidade ? ` ${rot.unidade}` : "";
        partes.push(`${rot.label}: ${v}${unid}`);
      }
      return `  ${i + 1}. ${formatarData(s.dataColeta)}: ${
        partes.length > 0 ? partes.join(" | ") : "Sem valores"
      }`;
    })
    .join("\n");

  const temSE = paciente.inscricaoTx?.situacaoEspecial === true;
  const diagnosticoBase =
    paciente.inscricaoTx?.diagnosticoBase ||
    paciente.avaliacaoClinica?.causaCirrose ||
    "Não informado";
  const isPosTx =
    paciente.statusTransplante === "pos" ||
    !!paciente.dataTransplante ||
    (Array.isArray(paciente.transplantes) && paciente.transplantes.length > 0);

  return `
ANÁLISE DE SOROLOGIAS — STATUS VIRAL/INFECCIOSO, RASTREIO E IMUNIZAÇÃO
=====================================================================

CONTEXTO DO PACIENTE:
- Sexo: ${paciente.sexo === "M" ? "Masculino" : "Feminino"}
- Diagnóstico de base: ${diagnosticoBase}
- Momento: ${isPosTx ? "PÓS-transplante" : "PRÉ-transplante / ambulatorial"}
- Situação especial de transplante: ${temSE ? "SIM" : "NÃO"}

HISTÓRICO DE SOROLOGIAS (${ordenadas.length} coletas no total${
    omitidas > 0
      ? `; exibindo as ${usadas.length} mais recentes — ${omitidas} mais antiga(s) omitida(s) por brevidade`
      : ""
  }, ordem cronológica):
${historico || "  Nenhuma sorologia registrada."}

═══════════════════════════════════════════════════════════════

INSTRUÇÕES — FOCO EXCLUSIVO NAS SOROLOGIAS:

Interprete APENAS as sorologias acima. Comente somente as categorias que têm ao
menos um valor. NÃO faça análise global do paciente. NÃO recomende imagem, biópsia
ou condutas terapêuticas globais (só o que é do escopo sorológico: elegibilidade
antiviral, indicação de vacina, rastreio, risco de infecção/reativação). NÃO invente
valores que não estejam nos dados; se um dado faltar, faça a leitura possível e
sinalize a lacuna como próximo passo — sem "reclamar" de ausências.

⚠️ ÍNDICE NUMÉRICO DE SOROLOGIA (COI/S-CO) — NÃO SUPONHA A DIREÇÃO DO CORTE:
quando o valor de uma sorologia vier como NÚMERO (ex.: "0,01") em vez de
"Reagente"/"Não Reagente", NÃO aplique automaticamente a regra usual "< 1,0 = não
reagente". O **anti-HBc TOTAL** é medido por ensaio COMPETITIVO em plataformas de
uso corrente: nele o laudo imprime "<1,0 = REAGENTE / >1,0 = NÃO REAGENTE" — ou
seja, índice BAIXO é POSITIVO. Como o índice sozinho não diz qual método foi usado,
trate-o como PENDENTE DE CONFERÊNCIA: diga qual é o valor, explique as duas leituras
possíveis e peça a confirmação da faixa de referência impressa no laudo — nunca
afirme "anti-HBc não reagente" a partir de um índice baixo isolado. (Caso real de
28/07/2026: anti-HBc total 0,01 = REAGENTE foi lido como negativo.) Um anti-HBc
falso-negativo esconde risco de reativação sob imunossupressão — erre para o lado
de conferir.

── A) HEPATITES VIRAIS ──
1. HEPATITE B — interprete o PERFIL, não campos isolados:
   - HBsAg reagente → infecção ATIVA (aguda ou crônica; ver Anti-HBc IgM, HBeAg,
     PCR/DNA e genótipo). HBsAg reagente + imunossupressão/Tx = risco de reativação
     → sinalizar necessidade de profilaxia antiviral (definida pela equipe).
   - HBsAg NÃO reagente + Anti-HBc IgG/total reagente + Anti-HBs reagente → infecção
     PASSADA resolvida (imune). Anti-HBc IgG isolado (Anti-HBs não reagente) →
     "core isolado": risco de reativação oculta sob imunossupressão — sinalizar.
   - Todos NÃO reagentes → SUSCETÍVEL: candidato a VACINAÇÃO pré-Tx.
   - Anti-HBs reagente isolado (Anti-HBc não reagente) → imune por VACINA.
2. HEPATITE C — Anti-HCV reagente = exposição; HCV-RNA/carga viral DETECTÁVEL =
   infecção ATIVA → indicação formal de tratamento (todo HCV-RNA detectável trata).
   Em cirrose descompensada, sinalizar que o regime evita inibidores de protease
   (conduta detalhada fica com a equipe). RNA indetectável pós-tratamento → checar SVR.
3. HEPATITE A / D / E: Anti-HAV IgG reagente = imune (por doença ou vacina); Anti-HAV
   IgM = infecção aguda. Anti-HDV só importa se HBsAg reagente (coinfecção delta).
   Anti-HEV conforme contexto.

── B) RASTREIO PRÉ-Tx E RISCO DE INFECÇÃO/REATIVAÇÃO PÓS-Tx ──
4. CMV e EBV (IgG/IgM/PCR): o status IgG do receptor define risco pós-Tx. Receptor
   CMV IgG NEGATIVO é o de MAIOR risco de doença por CMV (sobretudo se doador +) →
   sinalizar para estratégia de profilaxia/monitoramento (equipe). EBV IgG negativo
   → risco de PTLD pós-Tx. PCR detectável = replicação ativa.
5. Chagas (IgG/IgM): sorologia reagente → risco de REATIVAÇÃO sob imunossupressão →
   sinalizar vigilância. Estrongiloides IgG reagente → risco de HIPERINFECÇÃO sob
   corticoide/imunossupressão → sinalizar (tratamento definido pela equipe).
   TB latente (IGRA/Quantiferon ou PPD ≥ 5 mm em candidato a Tx) → sinalizar.

── C) IMUNIZAÇÃO (janela pré-Tx, antes da imunossupressão) ──
6. Marque como SUSCETÍVEL e candidato a vacinar quem estiver não-imune: Hepatite B
   (perfil suscetível), Hepatite A (Anti-HAV IgG não reagente), Rubéola (IgG não
   reagente). Vacinas de vírus vivo (ex.: tríplice viral) só ANTES do transplante.

── D) OUTRAS ──
7. HIV (Anti-HIV): reagente muda elegibilidade/condução (não contraindica per se hoje,
   mas requer manejo especializado). Sífilis: VDRL/RPR reagente confirmar com FTA-ABS
   (treponêmico). HTLV I/II reagente: relevante em doador/receptor. Toxo IgG+/IgM- =
   contato prévio (risco de reativação pós-Tx no SNC — vigilância).

── INTEGRAÇÃO ──
8. Feche com uma síntese: (a) status de elegibilidade/atividade das hepatites (com
   indicação de tratamento se houver RNA detectável), (b) o que exige VACINAÇÃO antes
   do Tx, (c) riscos de infecção/reativação pós-Tx a sinalizar à equipe, (d) lacunas
   de rastreio a completar (ex.: CMV/EBV/estrongiloides não dosados). Tendência quando
   houver série (ex.: soroconversão, queda de carga viral).
`.trim();
}
